import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  calculateCenterCrop,
  calculateLinkedDimension,
  calculateResizeDimensions,
  convertImageToJpeg,
  ConvertedImage,
  ImageColorSpace,
  ImageCrop,
  ImageDimensions,
} from '@/features/scheduler/utils/imageConversion';
import './ImageEditor.scss';

const INITIAL_SCALE = 100;
const MINIMUM_SCALE = 1;
const SCALE_STEP = 1;
const MINIMUM_CROP_POSITION = 0;
const MAXIMUM_CROP_POSITION = 100;
const DEFAULT_CROP_POSITION = 50;
const CROP_POSITION_STEP = 1;
const FEED_MINIMUM_WIDTH = 320;
const FEED_MAXIMUM_WIDTH = 1440;
const FEED_MAXIMUM_HEIGHT = 1800;
const MAXIMUM_DOWNSCALE_ATTEMPTS = 8;
const DOWNSCALE_SAFETY_FACTOR = 0.9;
const REELS_COVER_ASPECT_WIDTH = 9;
const REELS_COVER_ASPECT_HEIGHT = 16;
const IMAGE_MAXIMUM_FILE_SIZE = 8 * 1024 * 1024;
const IMAGE_PREVIEW_MAX_HEIGHT = 320;
const FEED_ASPECT_RATIO_RANGE = { minimum: 0.8, maximum: 1.91 };
const REELS_COVER_ASPECT_RATIO = REELS_COVER_ASPECT_WIDTH / REELS_COVER_ASPECT_HEIGHT;
const STORY_RECOMMENDED_ASPECT_RATIO = REELS_COVER_ASPECT_RATIO;
const STORY_ASPECT_RATIO_TOLERANCE = 0.01;
type ImageContext = 'feed' | 'stories' | 'reelsCover';

interface ImagePreview extends ConvertedImage {
  url: string;
  wasAutoDownscaled: boolean;
}

interface ImageEditorProps {
  sourceFile: File;
  onConfirm: (file: File, colorSpace: ImageColorSpace) => void;
  imageContext: ImageContext;
}

interface SwitchEventDetail {
  checked?: unknown;
}

interface ButtonEventTarget extends HTMLElement {
  addEventListener(type: 'bdsClick', listener: EventListener): void;
  removeEventListener(type: 'bdsClick', listener: EventListener): void;
}

const revokePreviewUrl = (previewUrl: { current: string | null }) => {
  if (previewUrl.current) URL.revokeObjectURL(previewUrl.current);
  previewUrl.current = null;
};

const replacePreview = (
  convertedImage: ConvertedImage,
  previewUrl: { current: string | null },
  setPreview: React.Dispatch<React.SetStateAction<ImagePreview | null>>,
  wasAutoDownscaled: boolean,
) => {
  revokePreviewUrl(previewUrl);
  const url = URL.createObjectURL(convertedImage.file);
  previewUrl.current = url;
  setPreview({ ...convertedImage, url, wasAutoDownscaled });
};

const getCropSourceDimensions = (crop: ImageCrop | null, original: ImageDimensions): ImageDimensions =>
  crop ? { width: crop.width, height: crop.height } : original;

const isFeedAspectRatioValid = (dimensions: ImageDimensions): boolean => {
  const aspectRatio = dimensions.width / dimensions.height;
  return aspectRatio >= FEED_ASPECT_RATIO_RANGE.minimum && aspectRatio <= FEED_ASPECT_RATIO_RANGE.maximum;
};

const isReelsCoverAspectRatioValid = (dimensions: ImageDimensions): boolean =>
  dimensions.width * REELS_COVER_ASPECT_HEIGHT === dimensions.height * REELS_COVER_ASPECT_WIDTH;

const calculateLinkedReelsCoverDimensions = (
  dimension: 'width' | 'height',
  value: number,
  maximum: ImageDimensions,
): ImageDimensions => {
  const requestedScale = Math.round(
    value / (dimension === 'width' ? REELS_COVER_ASPECT_WIDTH : REELS_COVER_ASPECT_HEIGHT),
  );
  const maximumScale = Math.floor(Math.min(
    maximum.width / REELS_COVER_ASPECT_WIDTH,
    maximum.height / REELS_COVER_ASPECT_HEIGHT,
  ));
  const scale = Math.max(1, Math.min(requestedScale, maximumScale));
  return {
    width: scale * REELS_COVER_ASPECT_WIDTH,
    height: scale * REELS_COVER_ASPECT_HEIGHT,
  };
};

const getFeedDimensions = (source: ImageDimensions): ImageDimensions => {
  const width = Math.min(FEED_MAXIMUM_WIDTH, Math.max(FEED_MINIMUM_WIDTH, source.width));
  return { width, height: Math.round(width / (source.width / source.height)) };
};

const getInitialDimensions = (
  original: ImageDimensions,
  imageContext: ImageEditorProps['imageContext'],
): ImageDimensions =>
  imageContext === 'feed' && isFeedAspectRatioValid(original)
    ? getFeedDimensions(original)
    : { width: original.width, height: original.height };

const calculateReelsCoverCrop = (source: ImageDimensions, position: number): ImageCrop => {
  const cropScale = Math.floor(Math.min(
    source.width / REELS_COVER_ASPECT_WIDTH,
    source.height / REELS_COVER_ASPECT_HEIGHT,
  ));
  const width = cropScale * REELS_COVER_ASPECT_WIDTH;
  const height = cropScale * REELS_COVER_ASPECT_HEIGHT;
  const horizontalRange = source.width - width;
  const verticalRange = source.height - height;
  const isHorizontalCrop = source.width / source.height > REELS_COVER_ASPECT_RATIO;

  return {
    x: isHorizontalCrop
      ? Math.round((horizontalRange * position) / MAXIMUM_CROP_POSITION)
      : Math.floor(horizontalRange / 2),
    y: isHorizontalCrop
      ? Math.floor(verticalRange / 2)
      : Math.round((verticalRange * position) / MAXIMUM_CROP_POSITION),
    width,
    height,
  };
};

const getInitialReelsCoverCrop = (
  source: ImageDimensions,
  imageContext: ImageContext,
): ImageCrop | null =>
  imageContext === 'reelsCover' && !isReelsCoverAspectRatioValid(source)
    ? calculateReelsCoverCrop(source, DEFAULT_CROP_POSITION)
    : null;

const prepareInitialPreview = async (
  sourceFile: File,
  original: ConvertedImage,
  dimensions: ImageDimensions,
  imageContext: ImageContext,
  crop: ImageCrop | null,
): Promise<PreparedPreview> => {
  const isResized = dimensions.width !== original.width || dimensions.height !== original.height;
  let convertedImage = original;
  if (isResized || crop) {
    convertedImage = crop
      ? await convertImageToJpeg(sourceFile, dimensions, crop)
      : await convertImageToJpeg(sourceFile, dimensions);
  }
  return imageContext === 'reelsCover'
    ? await preparePreviewWithinImageLimit(sourceFile, convertedImage, dimensions, crop)
    : { image: convertedImage, wasAutoDownscaled: false };
};

class ReelsCoverSizeLimitError extends Error {}

interface PreparedPreview {
  image: ConvertedImage;
  wasAutoDownscaled: boolean;
}

const areImageDimensionsEqual = (left: ImageDimensions, right: ImageDimensions): boolean =>
  left.width === right.width && left.height === right.height;

const getDownscaledDimensions = (
  dimensions: ImageDimensions,
  fileSize: number,
): ImageDimensions => {
  const scale = Math.min(
    DOWNSCALE_SAFETY_FACTOR,
    Math.sqrt(IMAGE_MAXIMUM_FILE_SIZE / fileSize) * DOWNSCALE_SAFETY_FACTOR,
  );
  const width = Math.max(1, Math.floor(dimensions.width * scale));
  const height = Math.max(1, Math.floor(dimensions.height * scale));
  return isReelsCoverAspectRatioValid(dimensions)
    ? calculateLinkedReelsCoverDimensions('width', width, dimensions)
    : { width, height };
};

const preparePreviewWithinImageLimit = async (
  sourceFile: File,
  convertedImage: ConvertedImage,
  dimensions: ImageDimensions,
  crop: ImageCrop | null,
): Promise<PreparedPreview> => {
  let currentImage = convertedImage;
  let currentDimensions = dimensions;
  let wasAutoDownscaled = false;
  let attempt = 0;
  while (currentImage.file.size > IMAGE_MAXIMUM_FILE_SIZE && attempt < MAXIMUM_DOWNSCALE_ATTEMPTS) {
    const nextDimensions = getDownscaledDimensions(currentDimensions, currentImage.file.size);
    if (areImageDimensionsEqual(nextDimensions, currentDimensions)) {
      throw new ReelsCoverSizeLimitError('The Reel cover cannot be reduced below the maximum file size');
    }
    currentImage = await convertImageToJpeg(sourceFile, nextDimensions, crop ?? undefined);
    currentDimensions = nextDimensions;
    wasAutoDownscaled = true;
    attempt += 1;
  }

  if (currentImage.file.size > IMAGE_MAXIMUM_FILE_SIZE) {
    throw new ReelsCoverSizeLimitError('The Reel cover cannot be reduced below the maximum file size');
  }
  return { image: currentImage, wasAutoDownscaled };
};

const handlePreviewPreparationError = (
  error: unknown,
  setDownscaleError: React.Dispatch<React.SetStateAction<boolean>>,
  setConversionError: React.Dispatch<React.SetStateAction<boolean>>,
): void => {
  if (error instanceof ReelsCoverSizeLimitError) {
    setDownscaleError(true);
    return;
  }
  console.error('Failed to prepare the scheduled image:', error);
  setConversionError(true);
};

const handlePreviewPreparationFailure = (
  error: unknown,
  isCurrentRequest: boolean,
  previewUrl: { current: string | null },
  setPreview: React.Dispatch<React.SetStateAction<ImagePreview | null>>,
  setDownscaleError: React.Dispatch<React.SetStateAction<boolean>>,
  setConversionError: React.Dispatch<React.SetStateAction<boolean>>,
): void => {
  if (!isCurrentRequest) return;
  revokePreviewUrl(previewUrl);
  setPreview(null);
  handlePreviewPreparationError(error, setDownscaleError, setConversionError);
};

const wasPreviewAutoDownscaled = (preview: ImagePreview | null): boolean =>
  preview?.wasAutoDownscaled ?? false;

type DimensionUpdateResult =
  | { type: 'invalid-input' }
  | { type: 'invalid-feed-ratio' }
  | { type: 'invalid-reels-cover-ratio' }
  | { type: 'valid'; dimensions: ImageDimensions };

const getDimensionMaximums = (
  source: ImageDimensions,
  imageContext: ImageEditorProps['imageContext'],
): ImageDimensions => ({
  width: imageContext === 'feed' ? FEED_MAXIMUM_WIDTH : source.width,
  height: imageContext === 'feed' ? FEED_MAXIMUM_HEIGHT : source.height,
});

const getBoundedDimensionValue = (
  dimension: 'width' | 'height',
  value: number,
  maximum: ImageDimensions,
  imageContext: ImageEditorProps['imageContext'],
): number => {
  const minimum = imageContext === 'feed' && dimension === 'width' ? FEED_MINIMUM_WIDTH : 1;
  const maximumValue = dimension === 'width' ? maximum.width : maximum.height;
  return Math.max(minimum, Math.min(value, maximumValue));
};

const calculateUpdatedDimensions = (
  dimension: 'width' | 'height',
  boundedValue: number,
  source: ImageDimensions,
  current: ImageDimensions,
  maximum: ImageDimensions,
  ratioLocked: boolean,
  imageContext: ImageEditorProps['imageContext'],
): ImageDimensions => {
  if (ratioLocked && imageContext === 'reelsCover') {
    return calculateLinkedReelsCoverDimensions(dimension, boundedValue, maximum);
  }
  if (ratioLocked) {
    return calculateLinkedDimension(source, dimension, { value: boundedValue, maximum });
  }

  return calculateResizeDimensions(
    source,
    {
      width: Math.min(dimension === 'width' ? boundedValue : current.width, maximum.width),
      height: Math.min(dimension === 'height' ? boundedValue : current.height, maximum.height),
    },
    maximum,
  );
};

const validateDimensionRatio = (
  dimensions: ImageDimensions,
  imageContext: ImageEditorProps['imageContext'],
): DimensionUpdateResult | null => {
  if (imageContext === 'feed' && !isFeedAspectRatioValid(dimensions)) {
    return { type: 'invalid-feed-ratio' };
  }
  if (imageContext === 'reelsCover' && !isReelsCoverAspectRatioValid(dimensions)) {
    return { type: 'invalid-reels-cover-ratio' };
  }
  return null;
};

const calculateDimensionUpdate = (
  dimension: 'width' | 'height',
  input: string,
  source: ImageDimensions,
  current: ImageDimensions,
  ratioLocked: boolean,
  imageContext: ImageEditorProps['imageContext'],
): DimensionUpdateResult => {
  const value = Number(input);
  if (!Number.isSafeInteger(value) || value < 1) return { type: 'invalid-input' };

  const maximum = getDimensionMaximums(source, imageContext);
  const boundedValue = getBoundedDimensionValue(dimension, value, maximum, imageContext);
  const dimensions = calculateUpdatedDimensions(
    dimension,
    boundedValue,
    source,
    current,
    maximum,
    ratioLocked,
    imageContext,
  );
  const invalidRatio = validateDimensionRatio(dimensions, imageContext);
  if (invalidRatio) return invalidRatio;

  return { type: 'valid', dimensions };
};

const calculateScaleDimensions = (
  source: ImageDimensions,
  scale: number,
  imageContext: ImageEditorProps['imageContext'],
): ImageDimensions => {
  if (imageContext === 'reelsCover') {
    const maximumScale = Math.floor(Math.min(
      source.width / REELS_COVER_ASPECT_WIDTH,
      source.height / REELS_COVER_ASPECT_HEIGHT,
    ));
    const scaleFactor = Math.max(1, Math.round(maximumScale * scale));
    return {
      width: scaleFactor * REELS_COVER_ASPECT_WIDTH,
      height: scaleFactor * REELS_COVER_ASPECT_HEIGHT,
    };
  }

  const maximumWidth = imageContext === 'feed' ? FEED_MAXIMUM_WIDTH : source.width;
  const maximumHeight = imageContext === 'feed' ? FEED_MAXIMUM_HEIGHT : source.height;
  const minimumWidth = imageContext === 'feed' ? FEED_MINIMUM_WIDTH : 1;
  return calculateResizeDimensions(
    source,
    {
      width: Math.min(maximumWidth, Math.max(minimumWidth, Math.round(source.width * scale))),
      height: Math.min(maximumHeight, Math.max(1, Math.round(source.height * scale))),
    },
    { width: maximumWidth, height: maximumHeight },
  );
};

const isValidImagePreview = (preview: ImagePreview | null, isConverting: boolean): boolean =>
  Boolean(
    preview &&
    !isConverting &&
    preview.file.size <= IMAGE_MAXIMUM_FILE_SIZE &&
    preview.file.type === 'image/jpeg',
  );

const canConfirmFeedImage = (dimensions: ImageDimensions | null): boolean =>
  Boolean(
    dimensions &&
    dimensions.width >= FEED_MINIMUM_WIDTH &&
    dimensions.width <= FEED_MAXIMUM_WIDTH &&
    isFeedAspectRatioValid(dimensions),
  );

const canConfirmReelsCover = (dimensions: ImageDimensions | null): boolean =>
  dimensions !== null && isReelsCoverAspectRatioValid(dimensions);

const canConfirmImage = (
  preview: ImagePreview | null,
  isConverting: boolean,
  dimensions: ImageDimensions | null,
  imageContext: ImageEditorProps['imageContext'],
): boolean => {
  if (!isValidImagePreview(preview, isConverting)) return false;
  const canConfirmByContext: Record<ImageContext, () => boolean> = {
    stories: () => true,
    reelsCover: () => canConfirmReelsCover(dimensions),
    feed: () => canConfirmFeedImage(dimensions),
  };
  return canConfirmByContext[imageContext]();
};

const isFeedRatioInvalid = (
  imageContext: ImageContext,
  crop: ImageCrop | null,
  sourceDimensions: ImageDimensions | null,
): boolean =>
  imageContext === 'feed' &&
  crop === null &&
  sourceDimensions !== null &&
  !isFeedAspectRatioValid(sourceDimensions);

const isReelsCoverRatioInvalid = (
  imageContext: ImageContext,
  sourceDimensions: ImageDimensions | null,
): boolean =>
  imageContext === 'reelsCover' &&
  sourceDimensions !== null &&
  !isReelsCoverAspectRatioValid(sourceDimensions);

const shouldShowReelsCoverCropPosition = (
  imageContext: ImageContext,
  sourceDimensions: ImageDimensions | null,
  ratioInvalid: boolean,
  crop: ImageCrop | null,
): boolean =>
  imageContext === 'reelsCover' &&
  sourceDimensions !== null &&
  (ratioInvalid || crop !== null);

const getReelsCoverCropFrame = (
  sourceDimensions: ImageDimensions | null,
  crop: ImageCrop | null,
  position: number,
): ReelsCoverCropFrame | null => {
  if (!sourceDimensions) return null;
  const cropArea = crop ?? calculateReelsCoverCrop(sourceDimensions, position);
  return {
    x: (cropArea.x / sourceDimensions.width) * 100,
    y: (cropArea.y / sourceDimensions.height) * 100,
    width: (cropArea.width / sourceDimensions.width) * 100,
    height: (cropArea.height / sourceDimensions.height) * 100,
  };
};

const getReelsCoverCropPositionLabel = (
  sourceDimensions: ImageDimensions | null,
  t: ImageEditorNoticesProps['t'],
): string => {
  if (sourceDimensions &&
    sourceDimensions.width / sourceDimensions.height > REELS_COVER_ASPECT_RATIO) {
    return t('upload.reelsCoverCropPositionHorizontal');
  }
  return t('upload.reelsCoverCropPositionVertical');
};

const getCurrentScale = (
  editableSource: ImageDimensions | null,
  dimensions: ImageDimensions | null,
): number => {
  if (!editableSource || !dimensions) return INITIAL_SCALE;
  return Math.max(1, Math.round(Math.sqrt(
    (dimensions.width / editableSource.width) * (dimensions.height / editableSource.height),
  ) * INITIAL_SCALE));
};

const getMinimumScale = (imageContext: ImageContext, editableSource: ImageDimensions | null): number =>
  imageContext === 'feed' && editableSource
    ? Math.ceil((FEED_MINIMUM_WIDTH / editableSource.width) * INITIAL_SCALE)
    : MINIMUM_SCALE;

const getMaximumScale = (imageContext: ImageContext, editableSource: ImageDimensions | null): number =>
  imageContext === 'feed' && editableSource
    ? Math.floor((FEED_MAXIMUM_WIDTH / editableSource.width) * INITIAL_SCALE)
    : INITIAL_SCALE;

const canConfirmCurrentImage = (
  preview: ImagePreview | null,
  isConverting: boolean,
  dimensions: ImageDimensions | null,
  imageContext: ImageContext,
  cropPreviewPending: boolean,
): boolean =>
  canConfirmImage(preview, isConverting, dimensions, imageContext) && !cropPreviewPending;

interface ImageEditorNoticesProps {
  sourceFile: File;
  sourceDimensions: ImageDimensions | null;
  dimensions: ImageDimensions | null;
  imageContext: ImageEditorProps['imageContext'];
  isFeedRatioInvalid: boolean;
  isReelsCoverRatioInvalid: boolean;
  ratioLocked: boolean;
  isConverting: boolean;
  onCenterCrop: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}

interface ImageNoticeProps {
  sourceFile: File;
  t: ImageEditorNoticesProps['t'];
}

const getSourceFormat = (sourceFile: File): string => {
  const sourceExtension = sourceFile.name.split('.').pop();
  if (sourceFile.type) return sourceFile.type;
  if (sourceExtension && sourceExtension !== sourceFile.name) return sourceExtension.toUpperCase();
  return 'unknown';
};

const isGifImage = (sourceFile: File): boolean =>
  sourceFile.type.toLowerCase() === 'image/gif' || sourceFile.name.toLowerCase().endsWith('.gif');

const ImageFormatNotice: React.FC<ImageNoticeProps> = ({ sourceFile, t }) => {
  return (
    <>
      {sourceFile.type.toLowerCase() !== 'image/jpeg' && (
        <output>{t('upload.imageFormatConversionNotice', { format: getSourceFormat(sourceFile) })}</output>
      )}
      <GifAnimationNotice sourceFile={sourceFile} t={t} />
    </>
  );
};

const GifAnimationNotice: React.FC<ImageNoticeProps> = ({ sourceFile, t }) =>
  isGifImage(sourceFile) ? <output>{t('upload.animatedGifFirstFrameNotice')}</output> : null;

interface ImageSizeNoticeProps extends ImageNoticeProps {
  imageContext: ImageContext;
}

const ImageSizeNotice: React.FC<ImageSizeNoticeProps> = ({ sourceFile, imageContext, t }) => {
  if (imageContext === 'reelsCover' || sourceFile.size <= IMAGE_MAXIMUM_FILE_SIZE) return null;

  return (
    <output>
      {t('upload.imageSizeResizeNotice', {
        size: (sourceFile.size / 1024 / 1024).toFixed(2),
        sizeBytes: sourceFile.size.toString(),
      })}
    </output>
  );
};

interface FeedImageNoticesProps {
  sourceDimensions: ImageDimensions | null;
  dimensions: ImageDimensions | null;
  isFeedRatioInvalid: boolean;
  isConverting: boolean;
  onCenterCrop: () => void;
  t: ImageEditorNoticesProps['t'];
}

const FeedImageNotices: React.FC<FeedImageNoticesProps> = ({
  sourceDimensions,
  dimensions,
  isFeedRatioInvalid,
  isConverting,
  onCenterCrop,
  t,
}) => (
  <>
    {sourceDimensions && sourceDimensions.width !== dimensions?.width && (
      <output>{t('upload.imageWidthResizeNotice', { width: sourceDimensions.width })}</output>
    )}
    {isFeedRatioInvalid && (
      <div role="alert" className="scheduled-image-editor_warning">
        <p>{t('upload.imageCropRequired', {
          ratio: sourceDimensions
            ? (sourceDimensions.width / sourceDimensions.height).toString()
            : undefined,
        })}</p>
        <button type="button" disabled={isConverting} onClick={onCenterCrop}>
          {t('upload.cropToFeedRatio')}
        </button>
      </div>
    )}
  </>
);

interface ReelsImageNoticeProps {
  sourceDimensions: ImageDimensions | null;
  isReelsCoverRatioInvalid: boolean;
  t: ImageEditorNoticesProps['t'];
}

const ReelsImageNotice: React.FC<ReelsImageNoticeProps> = ({
  sourceDimensions,
  isReelsCoverRatioInvalid,
  t,
}) => {
  if (!isReelsCoverRatioInvalid) return null;

  return (
    <div role="alert" className="scheduled-image-editor_warning">
      <p>{t('upload.reelsCoverRatioNeedsCrop', {
        ratio: sourceDimensions
          ? (sourceDimensions.width / sourceDimensions.height).toFixed(4)
          : undefined,
      })}</p>
    </div>
  );
};

const StoryImageNotice: React.FC<{ sourceDimensions: ImageDimensions | null; t: ImageEditorNoticesProps['t'] }> = ({
  sourceDimensions,
  t,
}) => {
  if (!sourceDimensions) return null;
  const aspectRatio = sourceDimensions.width / sourceDimensions.height;
  if (Math.abs(aspectRatio - STORY_RECOMMENDED_ASPECT_RATIO) <= STORY_ASPECT_RATIO_TOLERANCE) return null;

  return <output>{t('upload.storyImageRatioNotice')}</output>;
};

const UnlockedRatioNotice: React.FC<{ ratioLocked: boolean; t: ImageEditorNoticesProps['t'] }> = ({
  ratioLocked,
  t,
}) => ratioLocked ? null : <output>{t('upload.imageUnlockedRatioNotice')}</output>;

const ImageEditorNotices: React.FC<ImageEditorNoticesProps> = ({
  sourceFile,
  sourceDimensions,
  dimensions,
  imageContext,
  isFeedRatioInvalid,
  isReelsCoverRatioInvalid,
  ratioLocked,
  isConverting,
  onCenterCrop,
  t,
}) => (
  <>
    <ImageFormatNotice sourceFile={sourceFile} t={t} />
    <ImageSizeNotice sourceFile={sourceFile} imageContext={imageContext} t={t} />
    {imageContext === 'feed' && (
      <FeedImageNotices
        sourceDimensions={sourceDimensions}
        dimensions={dimensions}
        isFeedRatioInvalid={isFeedRatioInvalid}
        isConverting={isConverting}
        onCenterCrop={onCenterCrop}
        t={t}
      />
    )}
    <ReelsImageNotice
      sourceDimensions={sourceDimensions}
      isReelsCoverRatioInvalid={isReelsCoverRatioInvalid}
      t={t}
    />
    <UnlockedRatioNotice ratioLocked={ratioLocked} t={t} />
    {imageContext === 'stories' && <StoryImageNotice sourceDimensions={sourceDimensions} t={t} />}
  </>
);

interface ReelsCoverCropFrame {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ReelsCoverCropPreviewProps {
  isVisible: boolean;
  sourceDimensions: ImageDimensions | null;
  sourcePreviewUrl: string | null;
  cropFrame: ReelsCoverCropFrame | null;
}

const ReelsCoverCropPreview: React.FC<ReelsCoverCropPreviewProps> = ({
  isVisible,
  sourceDimensions,
  sourcePreviewUrl,
  cropFrame,
}) => {
  if (!isVisible || !sourceDimensions || !sourcePreviewUrl || !cropFrame) return null;

  return (
    <div
      className="scheduled-image-editor_crop-preview"
      data-testid="reels-cover-crop-preview"
      style={{
        aspectRatio: `${sourceDimensions.width} / ${sourceDimensions.height}`,
        width: `${sourceDimensions.width * Math.min(1, IMAGE_PREVIEW_MAX_HEIGHT / sourceDimensions.height)}px`,
      }}
      aria-hidden="true"
    >
      <img src={sourcePreviewUrl} alt="" />
      <svg viewBox="0 0 100 100" preserveAspectRatio="none">
        <path
          d={`M0 0H100V100H0Z M${cropFrame.x} ${cropFrame.y}H${cropFrame.x + cropFrame.width}V${cropFrame.y + cropFrame.height}H${cropFrame.x}Z`}
          fill="rgba(0, 0, 0, 0.45)"
          fillRule="evenodd"
        />
        <rect
          x={`${cropFrame.x}%`}
          y={`${cropFrame.y}%`}
          width={`${cropFrame.width}%`}
          height={`${cropFrame.height}%`}
          fill="none"
          stroke="white"
          strokeDasharray="2 1.5"
          strokeWidth="0.6"
          vectorEffect="non-scaling-stroke"
        />
        <path
          d={`M${cropFrame.x} 0V${cropFrame.y} M${cropFrame.x + cropFrame.width} 0V${cropFrame.y} M${cropFrame.x} 100V${cropFrame.y + cropFrame.height} M${cropFrame.x + cropFrame.width} 100V${cropFrame.y + cropFrame.height}`}
          stroke="white"
          strokeWidth="1.2"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
    </div>
  );
};

interface ReelsCoverCropControlProps {
  isVisible: boolean;
  position: number;
  positionLabel: string;
  isConverting: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPointerDown: (event: React.PointerEvent<HTMLInputElement>) => void;
  onKeyUp: () => void;
  onBlur: () => void;
}

const ReelsCoverCropControl: React.FC<ReelsCoverCropControlProps> = ({
  isVisible,
  position,
  positionLabel,
  isConverting,
  onChange,
  onPointerDown,
  onKeyUp,
  onBlur,
}) => {
  if (!isVisible) return null;

  return (
    <div className="scheduled-image-editor_crop-control">
      <label htmlFor="scheduled-image-crop-position">{positionLabel}</label>
      <input
        id="scheduled-image-crop-position"
        type="range"
        min={MINIMUM_CROP_POSITION}
        max={MAXIMUM_CROP_POSITION}
        step={CROP_POSITION_STEP}
        value={position}
        disabled={isConverting}
        onChange={onChange}
        onPointerDown={onPointerDown}
        onKeyUp={onKeyUp}
        onBlur={onBlur}
        aria-label={positionLabel}
      />
    </div>
  );
};

const getMinimumImageWidth = (imageContext: ImageContext): number =>
  imageContext === 'feed' ? FEED_MINIMUM_WIDTH : 1;

const getMaximumImageWidth = (
  imageContext: ImageContext,
  editableSource: ImageDimensions | null,
): number | undefined => imageContext === 'feed' ? FEED_MAXIMUM_WIDTH : editableSource?.width;

const getMaximumImageHeight = (
  imageContext: ImageContext,
  editableSource: ImageDimensions | null,
): number | undefined => imageContext === 'feed' ? FEED_MAXIMUM_HEIGHT : editableSource?.height;

interface ImageDimensionFieldsProps {
  isVisible: boolean;
  imageContext: ImageContext;
  editableSource: ImageDimensions | null;
  disabled: boolean;
  widthInput: string;
  heightInput: string;
  onChange: (dimension: 'width' | 'height', value: string) => void;
  onBlur: (dimension: 'width' | 'height') => void;
  t: ImageEditorNoticesProps['t'];
}

const ImageDimensionFields: React.FC<ImageDimensionFieldsProps> = ({
  isVisible,
  imageContext,
  editableSource,
  disabled,
  widthInput,
  heightInput,
  onChange,
  onBlur,
  t,
}) => isVisible ? (
  <div className="scheduled-image-editor_controls">
    <label htmlFor="scheduled-image-width">{t('upload.imageWidth')}</label>
    <input
      id="scheduled-image-width"
      type="number"
      min={getMinimumImageWidth(imageContext)}
      max={getMaximumImageWidth(imageContext, editableSource)}
      step={1}
      value={widthInput}
      disabled={disabled}
      onChange={(event) => onChange('width', event.currentTarget.value)}
      onBlur={() => onBlur('width')}
      aria-label={t('upload.imageWidth')}
    />

    <label htmlFor="scheduled-image-height">{t('upload.imageHeight')}</label>
    <input
      id="scheduled-image-height"
      type="number"
      min={1}
      max={getMaximumImageHeight(imageContext, editableSource)}
      step={1}
      value={heightInput}
      disabled={disabled}
      onChange={(event) => onChange('height', event.currentTarget.value)}
      onBlur={() => onBlur('height')}
      aria-label={t('upload.imageHeight')}
    />
  </div>
) : null;

interface ImageRatioLockProps {
  isVisible: boolean;
  ratioLocked: boolean;
  disabled: boolean;
  switchRef: React.RefObject<HTMLElement | null>;
  t: ImageEditorNoticesProps['t'];
}

const ImageRatioLock: React.FC<ImageRatioLockProps> = ({ isVisible, ratioLocked, disabled, switchRef, t }) => {
  if (!isVisible) return null;

  return (
    <div className="scheduled-image-editor_ratio-lock">
      <label htmlFor="scheduled-image-ratio-lock">{t('upload.lockAspectRatio')}</label>
      <bds-switch
        ref={switchRef as any}
        id="scheduled-image-ratio-lock"
        name="scheduled-image-ratio-lock"
        refer="scheduled-image-ratio-lock"
        checked={ratioLocked}
        disabled={disabled}
        aria-label={t('upload.lockAspectRatio')}
      />
    </div>
  );
};

interface ImageScaleControlProps {
  isVisible: boolean;
  scale: number;
  minimumScale: number;
  maximumScale: number;
  disabled: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onPointerDown: (event: React.PointerEvent<HTMLInputElement>) => void;
  onKeyUp: () => void;
  onBlur: () => void;
  t: ImageEditorNoticesProps['t'];
}

const ImageScaleControl: React.FC<ImageScaleControlProps> = ({
  isVisible,
  scale,
  minimumScale,
  maximumScale,
  disabled,
  onChange,
  onPointerDown,
  onKeyUp,
  onBlur,
  t,
}) => isVisible ? (
  <div className="scheduled-image-editor_scale-control">
    <label htmlFor="scheduled-image-scale">{t('upload.imageScale', { scale })}</label>
    <input
      id="scheduled-image-scale"
      type="range"
      min={minimumScale}
      max={Math.max(minimumScale, maximumScale)}
      step={SCALE_STEP}
      value={scale}
      disabled={disabled}
      onChange={onChange}
      onPointerDown={onPointerDown}
      onKeyUp={onKeyUp}
      onBlur={onBlur}
      aria-label={t('upload.imageScale', { scale })}
    />
  </div>
) : null;

interface ImagePreviewPanelProps {
  imageContext: ImageContext;
  preview: ImagePreview | null;
  previewScale: number;
  confirmDisabled: boolean;
  confirmButtonRef: React.RefObject<ButtonEventTarget | null>;
  t: ImageEditorNoticesProps['t'];
}

const ImagePreviewPanel: React.FC<ImagePreviewPanelProps> = ({
  imageContext,
  preview,
  previewScale,
  confirmDisabled,
  confirmButtonRef,
  t,
}) => {
  if (!preview) return null;
  const fileSizeMegabytes = (preview.file.size / 1024 / 1024).toFixed(2);
  const fileSizeBytes = preview.file.size.toString();

  return (
    <div className="scheduled-image-editor_preview">
      {imageContext !== 'reelsCover' && (
        <div className="scheduled-image-editor_preview-frame">
          <img
            src={preview.url}
            alt={t('upload.imagePreviewAlt')}
            style={{
              maxWidth: `${previewScale}%`,
              maxHeight: `${previewScale}%`,
            }}
          />
        </div>
      )}
      <p>{t('upload.imageDimensions', { width: preview.width, height: preview.height })}</p>
      <p>{t('upload.imageOutputSize', { size: fileSizeMegabytes, sizeBytes: fileSizeBytes })}</p>
      {preview.file.size > IMAGE_MAXIMUM_FILE_SIZE && (
        <p role="alert">{t('upload.imageOutputTooLarge')}</p>
      )}
      <bds-button
        ref={confirmButtonRef as any}
        variant="primary"
        disabled={confirmDisabled}
      >
        {t('upload.useConvertedImage')}
      </bds-button>
    </div>
  );
};

interface ReelsCoverSizeFeedbackProps {
  downscaleError: boolean;
  wasAutoDownscaled: boolean;
  t: ImageEditorNoticesProps['t'];
}

const ReelsCoverSizeFeedback: React.FC<ReelsCoverSizeFeedbackProps> = ({
  downscaleError,
  wasAutoDownscaled,
  t,
}) => {
  if (downscaleError) return <p role="alert">{t('upload.reelsCoverDownscaleFailed')}</p>;
  if (wasAutoDownscaled) return <output>{t('upload.reelsCoverAutoDownscaledNotice')}</output>;
  return null;
};

interface ImageEditorFeedbackProps {
  editError: boolean;
  imageContext: ImageContext;
  sourceDimensions: ImageDimensions | null;
  isConverting: boolean;
  conversionError: boolean;
  downscaleError: boolean;
  wasAutoDownscaled: boolean;
  t: ImageEditorNoticesProps['t'];
}

const ImageEditorFeedback: React.FC<ImageEditorFeedbackProps> = ({
  editError,
  imageContext,
  sourceDimensions,
  isConverting,
  conversionError,
  downscaleError,
  wasAutoDownscaled,
  t,
}) => (
  <>
    {editError && (
      <p role="alert">
        {imageContext === 'reelsCover' && sourceDimensions
          ? t('upload.reelsCoverRatioNeedsCrop', {
            ratio: (sourceDimensions.width / sourceDimensions.height).toFixed(4),
          })
          : t('upload.imageRatioNeedsCrop')}
      </p>
    )}
    {isConverting && <output>{t('upload.imageProcessing')}</output>}
    {conversionError && <p role="alert">{t('upload.imageConversionError')}</p>}
    <ReelsCoverSizeFeedback
      downscaleError={downscaleError}
      wasAutoDownscaled={wasAutoDownscaled}
      t={t}
    />
  </>
);

export const ImageEditor: React.FC<ImageEditorProps> = ({ sourceFile, onConfirm, imageContext }) => {
  const { t } = useTranslation();
  const [dimensions, setDimensions] = useState<ImageDimensions | null>(null);
  const [sourceDimensions, setSourceDimensions] = useState<ImageDimensions | null>(null);
  const [widthInput, setWidthInput] = useState('');
  const [heightInput, setHeightInput] = useState('');
  const [ratioLocked, setRatioLocked] = useState(true);
  const [crop, setCrop] = useState<ImageCrop | null>(null);
  const [reelsCoverCropPosition, setReelsCoverCropPosition] = useState(DEFAULT_CROP_POSITION);
  const [editError, setEditError] = useState(false);
  const [preview, setPreview] = useState<ImagePreview | null>(null);
  const [sourcePreviewUrl, setSourcePreviewUrl] = useState<string | null>(null);
  const [isConverting, setIsConverting] = useState(true);
  const [conversionError, setConversionError] = useState(false);
  const [downscaleError, setDownscaleError] = useState(false);
  const requestId = useRef(0);
  const previewUrl = useRef<string | null>(null);
  const pendingScalePreview = useRef<ImageDimensions | null>(null);
  const pendingCropPreview = useRef<{ dimensions: ImageDimensions; crop: ImageCrop } | null>(null);
  const activeScalePointer = useRef<number | null>(null);
  const activeCropPointer = useRef<number | null>(null);
  const switchRef = useRef<HTMLElement | null>(null);
  const confirmButtonRef = useRef<ButtonEventTarget | null>(null);

  const updatePreview = useCallback(async (requestedDimensions: ImageDimensions, requestedCrop: ImageCrop | null) => {
    const currentRequestId = ++requestId.current;
    setIsConverting(true);
    setConversionError(false);
    setDownscaleError(false);

    try {
      const convertedImage = await convertImageToJpeg(sourceFile, requestedDimensions, requestedCrop ?? undefined);
      let preparedPreview: PreparedPreview = { image: convertedImage, wasAutoDownscaled: false };
      if (imageContext === 'reelsCover') {
        preparedPreview = await preparePreviewWithinImageLimit(
          sourceFile,
          convertedImage,
          requestedDimensions,
          requestedCrop,
        );
      }
      if (currentRequestId !== requestId.current) return;
      replacePreview(
        preparedPreview.image,
        previewUrl,
        setPreview,
        preparedPreview.wasAutoDownscaled,
      );
    } catch (error) {
      handlePreviewPreparationFailure(
        error,
        currentRequestId === requestId.current,
        previewUrl,
        setPreview,
        setDownscaleError,
        setConversionError,
      );
    } finally {
      if (currentRequestId === requestId.current) setIsConverting(false);
    }
  }, [imageContext, sourceFile]);

  const prepareImage = useCallback(async () => {
    const currentRequestId = ++requestId.current;
    setIsConverting(true);
    setConversionError(false);
    setDownscaleError(false);
    setEditError(false);
    setDimensions(null);
    setSourceDimensions(null);
    setCrop(null);
    setReelsCoverCropPosition(DEFAULT_CROP_POSITION);
    pendingScalePreview.current = null;
    pendingCropPreview.current = null;
    activeCropPointer.current = null;

    try {
      const original = await convertImageToJpeg(sourceFile);
      if (currentRequestId !== requestId.current) return;
      const initialCrop = getInitialReelsCoverCrop(original, imageContext);
      const initialDimensions = initialCrop
        ? getCropSourceDimensions(initialCrop, original)
        : getInitialDimensions(original, imageContext);
      setSourceDimensions(original);
      setCrop(initialCrop);
      setDimensions(initialDimensions);
      setWidthInput(String(initialDimensions.width));
      setHeightInput(String(initialDimensions.height));
      const initialPreview = await prepareInitialPreview(
        sourceFile,
        original,
        initialDimensions,
        imageContext,
        initialCrop,
      );
      if (currentRequestId !== requestId.current) return;
      replacePreview(
        initialPreview.image,
        previewUrl,
        setPreview,
        initialPreview.wasAutoDownscaled,
      );
    } catch (error) {
      handlePreviewPreparationFailure(
        error,
        currentRequestId === requestId.current,
        previewUrl,
        setPreview,
        setDownscaleError,
        setConversionError,
      );
    } finally {
      if (currentRequestId === requestId.current) setIsConverting(false);
    }
  }, [imageContext, sourceFile]);

  useEffect(() => {
    void prepareImage();
    return () => {
      requestId.current += 1;
      revokePreviewUrl(previewUrl);
    };
  }, [prepareImage]);

  useEffect(() => {
    if (imageContext !== 'reelsCover') return;

    const sourceUrl = URL.createObjectURL(sourceFile);
    setSourcePreviewUrl(sourceUrl);
    return () => URL.revokeObjectURL(sourceUrl);
  }, [imageContext, sourceFile]);

  const normalizeDimensionInputs = (currentDimensions: ImageDimensions) => {
    setWidthInput(String(currentDimensions.width));
    setHeightInput(String(currentDimensions.height));
  };

  const handleInvalidDimensionUpdate = (
    result: Exclude<DimensionUpdateResult, { type: 'valid' }>,
    normalizeInput: boolean,
    currentDimensions: ImageDimensions,
  ) => {
    if (result.type === 'invalid-feed-ratio' || result.type === 'invalid-reels-cover-ratio') {
      setEditError(true);
    }
    if (normalizeInput) normalizeDimensionInputs(currentDimensions);
  };

  const applyDimensionUpdate = (
    result: Extract<DimensionUpdateResult, { type: 'valid' }>,
    dimension: 'width' | 'height',
    normalizeInput: boolean,
  ) => {
    setEditError(false);
    setDimensions(result.dimensions);
    if (normalizeInput || dimension !== 'width') setWidthInput(String(result.dimensions.width));
    if (normalizeInput || dimension !== 'height') setHeightInput(String(result.dimensions.height));
    void updatePreview(result.dimensions, crop);
  };

  const handleDimensionCommit = (dimension: 'width' | 'height', input: string, normalizeInput: boolean) => {
    if (!sourceDimensions || !dimensions) return;

    const editableSource = getCropSourceDimensions(crop, sourceDimensions);
    const result = calculateDimensionUpdate(dimension, input, editableSource, dimensions, ratioLocked, imageContext);
    if (result.type !== 'valid') {
      handleInvalidDimensionUpdate(result, normalizeInput, dimensions);
      return;
    }
    applyDimensionUpdate(result, dimension, normalizeInput);
  };

  const handleDimensionChange = (dimension: 'width' | 'height', input: string) => {
    if (dimension === 'width') {
      setWidthInput(input);
    } else {
      setHeightInput(input);
    }
    handleDimensionCommit(dimension, input, false);
  };

  const handleDimensionBlur = (dimension: 'width' | 'height') => {
    const input = dimension === 'width' ? widthInput : heightInput;
    handleDimensionCommit(dimension, input, true);
  };

  const handleScaleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!sourceDimensions) return;
    const baseDimensions = getCropSourceDimensions(crop, sourceDimensions);
    const scale = Number(event.currentTarget.value) / INITIAL_SCALE;
    const nextDimensions = calculateScaleDimensions(baseDimensions, scale, imageContext);
    if (imageContext === 'feed' && !isFeedAspectRatioValid(nextDimensions)) return;
    if (imageContext === 'reelsCover' && !isReelsCoverAspectRatioValid(nextDimensions)) return;

    requestId.current += 1;
    setIsConverting(false);
    setConversionError(false);
    setEditError(false);
    setDimensions(nextDimensions);
    setWidthInput(String(nextDimensions.width));
    setHeightInput(String(nextDimensions.height));
    pendingScalePreview.current = nextDimensions;
  };

  const handleScaleCommit = useCallback(() => {
    const nextDimensions = pendingScalePreview.current;
    if (!nextDimensions) return;

    pendingScalePreview.current = null;
    void updatePreview(nextDimensions, crop);
  }, [crop, updatePreview]);

  const handleScalePointerDown = (event: React.PointerEvent<HTMLInputElement>) => {
    activeScalePointer.current = event.pointerId;
  };

  const handleScaleBlur = () => {
    if (activeScalePointer.current === null) handleScaleCommit();
  };

  useEffect(() => {
    const handleScalePointerEnd = (event: PointerEvent) => {
      if (activeScalePointer.current !== event.pointerId) return;

      activeScalePointer.current = null;
      handleScaleCommit();
    };

    globalThis.addEventListener('pointerup', handleScalePointerEnd);
    globalThis.addEventListener('pointercancel', handleScalePointerEnd);
    return () => {
      globalThis.removeEventListener('pointerup', handleScalePointerEnd);
      globalThis.removeEventListener('pointercancel', handleScalePointerEnd);
    };
  }, [handleScaleCommit]);

  const handleCenterCrop = () => {
    if (!sourceDimensions || imageContext !== 'feed') return;
    const nextCrop = calculateCenterCrop(sourceDimensions, FEED_ASPECT_RATIO_RANGE);
    const croppedDimensions = getCropSourceDimensions(nextCrop, sourceDimensions);
    const nextDimensions = getFeedDimensions(croppedDimensions);
    setCrop(nextCrop);
    setDimensions(nextDimensions);
    setWidthInput(String(nextDimensions.width));
    setHeightInput(String(nextDimensions.height));
    setEditError(false);
    void updatePreview(nextDimensions, nextCrop);
  };

  const handleReelsCoverCropPositionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!sourceDimensions) return;

    const requestedPosition = Number(event.currentTarget.value);
    if (!Number.isFinite(requestedPosition)) return;
    const position = Math.max(
      MINIMUM_CROP_POSITION,
      Math.min(requestedPosition, MAXIMUM_CROP_POSITION),
    );
    const nextCrop = calculateReelsCoverCrop(sourceDimensions, position);
    const nextDimensions = getCropSourceDimensions(nextCrop, sourceDimensions);

    pendingScalePreview.current = null;
    pendingCropPreview.current = { dimensions: nextDimensions, crop: nextCrop };
    setReelsCoverCropPosition(position);
    setCrop(nextCrop);
    setDimensions(nextDimensions);
    setWidthInput(String(nextDimensions.width));
    setHeightInput(String(nextDimensions.height));
    setEditError(false);
  };

  const handleReelsCoverCropCommit = useCallback(() => {
    const pendingPreview = pendingCropPreview.current;
    if (!pendingPreview) return;

    pendingCropPreview.current = null;
    void updatePreview(pendingPreview.dimensions, pendingPreview.crop);
  }, [updatePreview]);

  const handleCropPointerDown = (event: React.PointerEvent<HTMLInputElement>) => {
    activeCropPointer.current = event.pointerId;
  };

  const handleCropBlur = () => {
    if (activeCropPointer.current === null) handleReelsCoverCropCommit();
  };

  useEffect(() => {
    const handleCropPointerEnd = (event: PointerEvent) => {
      if (activeCropPointer.current !== event.pointerId) return;

      activeCropPointer.current = null;
      handleReelsCoverCropCommit();
    };

    globalThis.addEventListener('pointerup', handleCropPointerEnd);
    globalThis.addEventListener('pointercancel', handleCropPointerEnd);
    return () => {
      globalThis.removeEventListener('pointerup', handleCropPointerEnd);
      globalThis.removeEventListener('pointercancel', handleCropPointerEnd);
    };
  }, [handleReelsCoverCropCommit]);

  useEffect(() => {
    const switchElement = switchRef.current;
    const confirmButton = confirmButtonRef.current;
    if (!confirmButton) return;

    const handleRatioLock = (event: Event) => {
      const checked = (event as CustomEvent<SwitchEventDetail>).detail?.checked;
      if (typeof checked === 'boolean') setRatioLocked(checked);
    };
    const handleConfirm = () => {
      if (canConfirm && preview) onConfirm(preview.file, preview.colorSpace);
    };

    switchElement?.addEventListener('bdsChange', handleRatioLock);
    confirmButton.addEventListener('bdsClick', handleConfirm);
    return () => {
      switchElement?.removeEventListener('bdsChange', handleRatioLock);
      confirmButton.removeEventListener('bdsClick', handleConfirm);
    };
  });

  const editableSource = sourceDimensions ? getCropSourceDimensions(crop, sourceDimensions) : null;
  const feedRatioInvalid = isFeedRatioInvalid(imageContext, crop, sourceDimensions);
  const reelsCoverRatioInvalid = isReelsCoverRatioInvalid(imageContext, sourceDimensions);
  const showReelsCoverCropPosition = shouldShowReelsCoverCropPosition(
    imageContext,
    sourceDimensions,
    reelsCoverRatioInvalid,
    crop,
  );
  const reelsCoverCropFrame = getReelsCoverCropFrame(sourceDimensions, crop, reelsCoverCropPosition);
  const reelsCoverCropPositionLabel = getReelsCoverCropPositionLabel(sourceDimensions, t);
  const isRatioInvalid = feedRatioInvalid || reelsCoverRatioInvalid;
  const scale = getCurrentScale(editableSource, dimensions);
  const minimumScale = getMinimumScale(imageContext, editableSource);
  const maximumScale = getMaximumScale(imageContext, editableSource);
  const previewScale = Math.min(scale, INITIAL_SCALE);
  const canConfirm = canConfirmCurrentImage(
    preview,
    isConverting,
    dimensions,
    imageContext,
    pendingCropPreview.current !== null,
  );
  const disableDimensionControls = !dimensions || isRatioInvalid;
  const disableRatioLock = disableDimensionControls || isConverting;

  return (
    <section className="scheduled-image-editor" aria-labelledby="scheduled-image-editor-title">
      <bds-typo id="scheduled-image-editor-title" tag="h4" variant="fs-20" bold="bold">
        {t('upload.imageEditorTitle')}
      </bds-typo>
      <p>{t('upload.imageEditorDescription')}</p>

      <ImageEditorNotices
        sourceFile={sourceFile}
        sourceDimensions={sourceDimensions}
        dimensions={dimensions}
        imageContext={imageContext}
        isFeedRatioInvalid={feedRatioInvalid}
        isReelsCoverRatioInvalid={reelsCoverRatioInvalid}
        ratioLocked={ratioLocked}
        isConverting={isConverting}
        onCenterCrop={handleCenterCrop}
        t={t}
      />
      <ReelsCoverCropPreview
        isVisible={imageContext === 'reelsCover'}
        sourceDimensions={sourceDimensions}
        sourcePreviewUrl={sourcePreviewUrl}
        cropFrame={reelsCoverCropFrame}
      />
      <ReelsCoverCropControl
        isVisible={showReelsCoverCropPosition}
        position={reelsCoverCropPosition}
        positionLabel={reelsCoverCropPositionLabel}
        isConverting={isConverting}
        onChange={handleReelsCoverCropPositionChange}
        onPointerDown={handleCropPointerDown}
        onKeyUp={handleReelsCoverCropCommit}
        onBlur={handleCropBlur}
      />
      <p>{t('upload.imageColorSpaceNotice')}</p>
      <ImageDimensionFields
        isVisible={imageContext !== 'reelsCover'}
        imageContext={imageContext}
        editableSource={editableSource}
        disabled={disableDimensionControls}
        widthInput={widthInput}
        heightInput={heightInput}
        onChange={handleDimensionChange}
        onBlur={handleDimensionBlur}
        t={t}
      />
      <ImageRatioLock
        isVisible={imageContext !== 'reelsCover'}
        ratioLocked={ratioLocked}
        disabled={disableRatioLock}
        switchRef={switchRef}
        t={t}
      />
      <ImageScaleControl
        isVisible={imageContext !== 'reelsCover'}
        scale={scale}
        minimumScale={minimumScale}
        maximumScale={maximumScale}
        disabled={disableDimensionControls}
        onChange={handleScaleChange}
        onPointerDown={handleScalePointerDown}
        onKeyUp={handleScaleCommit}
        onBlur={handleScaleBlur}
        t={t}
      />
      <ImageEditorFeedback
        editError={editError}
        imageContext={imageContext}
        sourceDimensions={sourceDimensions}
        isConverting={isConverting}
        conversionError={conversionError}
        downscaleError={downscaleError}
        wasAutoDownscaled={wasPreviewAutoDownscaled(preview)}
        t={t}
      />
      <ImagePreviewPanel
        imageContext={imageContext}
        preview={preview}
        previewScale={previewScale}
        confirmDisabled={!canConfirm}
        confirmButtonRef={confirmButtonRef}
        t={t}
      />
    </section>
  );
};
